import React, { useCallback, useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl';
import type { PickingInfo } from 'deck.gl';
import { useMmsLayers } from './useMmsLayers';
import type { MmsLine, MmsNode, UseMmsMapDataResult } from './useMmsMapData';
import type { ViewState } from './useMapViewState';
import type { MmsAssetAttribute, MmsAttributeValue, MmsPopupAdditionalData, OpenPopupParams } from './useMapPopup';
import type { PopupData } from '../mapModes/MapModeStrategy';

interface UseMmsInteractionsParams {
	mmsMapData: UseMmsMapDataResult;
	activatedMmsMode: boolean;
	selectedMmsId: string | undefined;
	viewState: ViewState;
	setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
	mapRef: React.RefObject<MapRef>;
	openPopup: (params: OpenPopupParams) => void;
	hidePopup: () => void;
}

export function useMmsInteractions({
	mmsMapData,
	activatedMmsMode,
	selectedMmsId,
	viewState,
	setViewState,
	mapRef,
	openPopup,
	hidePopup,
}: UseMmsInteractionsParams) {
	const mmsAutoZoomedRef = useRef(false);

	const buildAssetAdditionalData = useCallback((asset: PopupData): MmsPopupAdditionalData => ({
		mmsId: selectedMmsId,
		onClose: () => hidePopup(),
		setAttributesValue: (attributes: MmsAssetAttribute[]) => {
			mmsMapData.setAssetAttributes(asset.assetId, attributes);
		},
		onUpdateAttributeValue: (attributeName: string, newValue: MmsAttributeValue) => {
			mmsMapData.updateAssetAttribute(asset.assetId, attributeName, newValue);
		},
		onDelete: () => {
			mmsMapData.markAssetDeleted(asset.assetId);
			hidePopup();
		},
		onRestore: () => {
			mmsMapData.restoreAsset(asset.assetId);
			hidePopup();
		},
	}), [mmsMapData, selectedMmsId, hidePopup]);

	const handleMmsNodeClick = useCallback((node: MmsNode) => {
		if (!mmsMapData.assetsData) return;
		const asset: PopupData | undefined = mmsMapData.assetsData.assets?.find(
			(a: PopupData) => a.assetId === node.assetId,
		);

		if (asset) {
			openPopup({
				data: asset,
				coordinates: [node.longitude, node.latitude],
				additionalData: buildAssetAdditionalData(asset),
			});
		}
	}, [mmsMapData, buildAssetAdditionalData, openPopup]);

	const handleMmsLineClick = useCallback((line: MmsLine) => {
		const asset: PopupData | undefined = mmsMapData.assetsData?.assets.find(
			(a: PopupData) => a.assetId === line.assetId,
		);
		if (asset) {
			const centerCoord = line.coordinates?.[Math.floor((line.coordinates.length - 1) / 2)] || line.coordinates?.[0];
			openPopup({
				data: asset,
				coordinates: centerCoord,
				additionalData: buildAssetAdditionalData(asset),
			});
		}
	}, [mmsMapData, buildAssetAdditionalData, openPopup]);

	const handleMmsNodeDrag = useCallback((node: MmsNode, lng: number, lat: number) => {
		mmsMapData.updateNodePosition(node.assetId, lng, lat);
	}, [mmsMapData]);

	const handleMmsNodeDragEnd = useCallback((node: MmsNode, lng: number, lat: number) => {
		mmsMapData.updateNodePosition(node.assetId, lng, lat);
	}, [mmsMapData]);

	const {
		layers: mmsLayers,
		isDraggingNode: isMmsDraggingNode,
		isDraggingNodeRef,
		isHoveringNodeRef,
		handleDragStart,
		handleDrag,
		handleDragEnd,
	} = useMmsLayers({
		allNodes: mmsMapData.allNodes,
		allLines: mmsMapData.allLines,
		nodeMap: mmsMapData.nodeMap,
		viewState,
		enabled: activatedMmsMode,
		mapRef,
		onNodeClick: handleMmsNodeClick,
		onLineClick: handleMmsLineClick,
		onNodeDrag: handleMmsNodeDrag,
		onNodeDragEnd: handleMmsNodeDragEnd,
	});

	const handlePointerDown = useCallback(() => {
		if (activatedMmsMode && isHoveringNodeRef?.current && viewState.zoom >= 13.6) {
			isDraggingNodeRef.current = true;
		}
	}, [activatedMmsMode, viewState.zoom, isHoveringNodeRef, isDraggingNodeRef]);

	const handlePointerUp = useCallback(() => {
		if (activatedMmsMode) {
			isDraggingNodeRef.current = false;
		}
	}, [activatedMmsMode, isDraggingNodeRef]);

	useEffect(() => {
		const map = mapRef.current?.getMap?.();
		if (!map?.dragPan) return;
		if (activatedMmsMode) {
			map.dragPan.disable();
		} else {
			map.dragPan.enable();
		}
	}, [activatedMmsMode]);

	useEffect(() => {
		if (!activatedMmsMode) {
			mmsAutoZoomedRef.current = false;
		}
	}, [activatedMmsMode]);

	useEffect(() => {
		if (activatedMmsMode && mmsMapData.dataCenter && !mmsAutoZoomedRef.current) {
			const { longitude, latitude } = mmsMapData.dataCenter;
			if (longitude && latitude) {
				mmsAutoZoomedRef.current = true;
				setViewState((prev) => ({
					...prev,
					longitude,
					latitude,
					zoom: 14,
				}));
			}
		}
	}, [activatedMmsMode, mmsMapData.dataCenter]);

	const handleMmsMapClick = useCallback((info: PickingInfo) => {
		if (activatedMmsMode && info.picked && info.object) {
			if (info.object.assetId && info.layer?.id === 'mms-nodes-layer') {
				handleMmsNodeClick(info.object);
				return true;
			}
			if (info.object.assetId && info.layer?.id === 'mms-lines-layer') {
				handleMmsLineClick(info.object);
				return true;
			}
		}
		return false;
	}, [activatedMmsMode, handleMmsNodeClick, handleMmsLineClick]);

	const getMmsTooltip = useCallback(({ object, layer }: PickingInfo) => {
		if (!object || viewState.zoom < 13.6) return null;
		if (layer?.id === 'mms-nodes-layer') {
			return {
				html: `<div style="background: #232B36; color: white; padding: 8px; border-radius: 10px; font-size: 12px; pointer-events: none;">
					<span><strong>Name:</strong> ${object.label || object.assetId}</span><br/>
					<span><strong>Type:</strong> ${object.type}</span>
				</div>`,
				style: {
					backgroundColor: 'transparent',
					fontSize: '12px',
					zIndex: '99',
					pointerEvents: 'none',
				},
			};
		}
		if (layer?.id === 'mms-lines-layer') {
			return {
				html: `<div style="background: #232B36; color: white; padding: 8px; border-radius: 10px; font-size: 12px; pointer-events: none;">
					<span><strong>Name:</strong> ${object.id}</span><br/>
					<span><strong>Type:</strong> ${object.type}</span>
				</div>`,
				style: {
					backgroundColor: 'transparent',
					fontSize: '12px',
					zIndex: '99',
					pointerEvents: 'none',
				},
			};
		}
		return null;
	}, [viewState.zoom]);

	return {
		mmsLayers,
		isMmsDraggingNode,
		isDraggingNodeRef,
		handlePointerDown,
		handlePointerUp,
		handleDragStart,
		handleDrag,
		handleDragEnd,
		handleMmsMapClick,
		getMmsTooltip,
	};
}
